import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, Phone, PhoneCall, PhoneOff, Mic, MicOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTwilioDevice } from '@/hooks/useTwilioDevice';
import { CandidateContext, JobContext } from './types';

interface InitiateCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateId: string;
  candidate: CandidateContext;
  job?: JobContext | null;
  onCallInitiated?: () => void;
}

export function InitiateCallDialog({
  open,
  onOpenChange,
  candidateId,
  candidate,
  job,
  onCallInitiated,
}: InitiateCallDialogProps) {
  const [recordCall, setRecordCall] = useState(true);
  const [isInitializing, setIsInitializing] = useState(false);
  
  const { toast } = useToast();
  
  const {
    callStatus,
    isMuted,
    formattedDuration,
    initDevice,
    makeCall,
    hangUp,
    toggleMute,
  } = useTwilioDevice({
    onCallStatusChange: (status) => {
      console.log('Call status changed:', status);
      if (status === 'connected') {
        onCallInitiated?.();
      }
      // Auto-close dialog when call ends
      if (status === 'disconnected') {
        setTimeout(() => {
          onOpenChange(false);
        }, 1500); // Brief delay to show "Call ended" state
      }
    },
    onError: (error) => {
      console.error('Twilio error:', error);
      setIsInitializing(false);
    },
  });

  // Reset state when dialog opens
  useEffect(() => {
    if (!open) {
      setIsInitializing(false);
    }
  }, [open]);

  const handleInitiateCall = async () => {
    if (!candidate.phone) {
      toast({
        title: 'Error',
        description: 'Candidate does not have a phone number',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsInitializing(true);
      
      await makeCall(candidate.phone, {
        candidateId,
        jobId: job?.id,
        record: recordCall,
      });
      
      toast({
        title: 'Calling...',
        description: `Calling ${candidate.firstName || ''} ${candidate.lastName || ''}`,
      });
    } catch (error: any) {
      console.error('Error initiating call:', error);
      toast({
        title: 'Call failed',
        description: error.message || 'Failed to initiate call',
        variant: 'destructive',
      });
    } finally {
      setIsInitializing(false);
    }
  };

  const handleHangUp = () => {
    hangUp();
    toast({
      title: 'Call ended',
      description: `Call with ${candidate.firstName || ''} ${candidate.lastName || ''} ended`,
    });
  };

  const handleClose = () => {
    if (callStatus === 'connected' || callStatus === 'ringing' || callStatus === 'connecting') {
      // Don't close if there's an active call
      return;
    }
    onOpenChange(false);
  };

  const getStatusIcon = () => {
    switch (callStatus) {
      case 'connecting':
        return <Loader2 className="h-12 w-12 animate-spin text-primary" />;
      case 'ringing':
        return <Phone className="h-12 w-12 text-yellow-500 animate-pulse" />;
      case 'connected':
        return <PhoneCall className="h-12 w-12 text-green-500 animate-pulse" />;
      case 'error':
        return <PhoneOff className="h-12 w-12 text-destructive" />;
      default:
        return <Phone className="h-12 w-12 text-muted-foreground" />;
    }
  };

  const getStatusText = () => {
    switch (callStatus) {
      case 'connecting':
        return 'Connecting...';
      case 'ringing':
        return 'Ringing...';
      case 'connected':
        return `Connected • ${formattedDuration}`;
      case 'disconnected':
        return 'Call ended';
      case 'error':
        return 'Call failed';
      default:
        return 'Ready to call';
    }
  };

  const isCallActive = callStatus === 'connected' || callStatus === 'ringing' || callStatus === 'connecting';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" onPointerDownOutside={(e) => isCallActive && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Call Candidate</DialogTitle>
          <DialogDescription>
            {isCallActive 
              ? 'Call in progress...' 
              : `Initiate a call to ${candidate.firstName} ${candidate.lastName}`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {/* Call Status Display */}
          <div className="flex flex-col items-center justify-center space-y-4 py-4">
            <div className="p-4 rounded-full bg-muted">
              {getStatusIcon()}
            </div>
            
            <div className="text-center">
              <p className="text-lg font-semibold">
                {candidate.firstName} {candidate.lastName}
              </p>
              <p className="text-muted-foreground">{candidate.phone}</p>
              <p className="text-sm text-muted-foreground mt-2">{getStatusText()}</p>
            </div>
          </div>

          {/* Call Controls (visible during active call) */}
          {callStatus === 'connected' && (
            <div className="flex justify-center gap-4 mt-4">
              <Button
                variant={isMuted ? "destructive" : "outline"}
                size="icon"
                className="h-12 w-12 rounded-full"
                onClick={toggleMute}
              >
                {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </Button>
            </div>
          )}

          {/* Options (visible before call) */}
          {callStatus === 'idle' && (
            <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30 mt-4">
              <div className="space-y-0.5">
                <Label htmlFor="record-call" className="text-sm font-medium">
                  Record call
                </Label>
                <p className="text-xs text-muted-foreground">
                  Recording will be transcribed automatically
                </p>
              </div>
              <Switch
                id="record-call"
                checked={recordCall}
                onCheckedChange={setRecordCall}
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {!isCallActive && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          )}
          
          {callStatus === 'idle' && (
            <Button 
              onClick={handleInitiateCall} 
              disabled={isInitializing || !candidate.phone}
              className="gap-2"
            >
              {isInitializing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Phone className="h-4 w-4" />
              )}
              Call Now
            </Button>
          )}
          
          {isCallActive && (
            <Button 
              variant="destructive"
              onClick={handleHangUp}
              className="gap-2 w-full"
            >
              <PhoneOff className="h-4 w-4" />
              End Call
            </Button>
          )}
          
          {callStatus === 'disconnected' && (
            <Button onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
